import { useContext, useEffect, useRef } from 'react';
import Footer from './components/footer/footer.component';
import Navigation from './components/navigation/navigation.component';
import { CartContext } from './contexts/cart.context';
import Router from './router';
import SHOP_DATA from './shop-data';

const CATALOG_IDS = new Set(
  SHOP_DATA.flatMap((category) => category.items.map((item) => item.id))
);

const readSavedCart = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('cartItems'));
    return Array.isArray(saved) ? saved : [];
  } catch {
    // Storage illisible : on repart d'un panier vide plutôt que de casser le rendu.
    return [];
  }
};

const App = () => {
  const { isCartOpen, setIsCartOpen, setCartItems } = useContext(CartContext);

  // Lu une seule fois, au premier rendu : c'est ce qui garantit qu'on voit le panier
  // sauvegardé avant que le provider ne commence à le réécrire.
  const savedItems = useRef(readSavedCart());

  const toggleIsCartOpen = () => {
    if (isCartOpen) {
      setIsCartOpen(false);
    }
  };

  useEffect(() => {
    if (!isCartOpen) return;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsCartOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isCartOpen, setIsCartOpen]);

  useEffect(() => {
    const items = savedItems.current;
    if (!items.length) return;

    // Un panier laissé en localStorage peut contenir des produits retirés du catalogue :
    // le serveur les refuserait au paiement (unknown_product). On les écarte au chargement.
    // L'id d'une fleur au panier encode le palier en décimale (ex: 417.2), d'où le trunc.
    const validItems = items.filter((item) =>
      CATALOG_IDS.has(Math.trunc(item?.id))
    );

    // Rien à restaurer si tout a été écarté : le provider persistera le panier vide.
    if (validItems.length) {
      setCartItems(validItems);
    }
  }, [setCartItems]);

  return (
    // `presentation` : cette enveloppe n'est qu'un capteur de clic pour refermer
    // le panier, elle ne porte aucune sémantique. L'accès clavier passe par Échap,
    // géré au-dessus, et non par cet élément.
    // biome-ignore lint/a11y/noStaticElementInteractions: capteur de clic hors panier, équivalent clavier fourni par Échap
    <div onClick={toggleIsCartOpen} role="presentation">
      <Navigation />
      <Router />
      <Footer />
    </div>
  );
};

export default App;
